import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

const NO_MATCH_VALUE = "__NO_MATCH__";

const normalizeName = (value) =>
    (value || "").trim().toLowerCase().replace(/\s+/g, " ");
const toIdKey = (value) => String(value);

export default function PipelineStepMappingModal({
    open,
    setOpen,
    previewData,
    onConfirm,
    onCancel,
}) {
    const [selectedMappings, setSelectedMappings] = useState({});

    const oldSteps = previewData?.oldSteps || [];
    const newSteps = previewData?.newSteps || [];
    const canAutoMatch = oldSteps.length === newSteps.length;

    useEffect(() => {
        if (open) {
            setSelectedMappings({});
        }
    }, [open, previewData]);

    const hasSelectionForAllOldSteps = useMemo(() => {
        return oldSteps.every((step) =>
            Object.prototype.hasOwnProperty.call(selectedMappings, step.id),
        );
    }, [oldSteps, selectedMappings]);

    const getUsedNewStepIds = (currentOldStepId) => {
        return Object.entries(selectedMappings)
            .filter(
                ([oldId, newId]) =>
                    oldId !== toIdKey(currentOldStepId) &&
                    newId !== null &&
                    typeof newId !== "undefined" &&
                    newId !== "",
            )
            .map(([, newId]) => toIdKey(newId));
    };

    const handleChangeMapping = (oldStepId, value) => {
        const nextValue = value === NO_MATCH_VALUE ? null : value;

        if (nextValue !== null) {
            const isAlreadyUsed = getUsedNewStepIds(oldStepId).includes(
                toIdKey(nextValue),
            );
            if (isAlreadyUsed) {
                toast.error("Each new step can only be used once.");
                return;
            }
        }

        setSelectedMappings((prev) => ({
            ...prev,
            [oldStepId]: nextValue,
        }));
    };

    const handleAutoMatch = () => {
        if (!canAutoMatch) return;

        const suggestions = previewData?.suggestedMappings || [];
        const nextMappings = {};

        if (suggestions.length > 0) {
            const usedNewIds = new Set();
            suggestions.forEach((mapping) => {
                if (!mapping || typeof mapping.oldServiceTaskId === "undefined") {
                    return;
                }

                const mappedNewId =
                    mapping.newServiceTaskId === null
                        ? null
                        : mapping.newServiceTaskId;

                if (mappedNewId !== null && usedNewIds.has(toIdKey(mappedNewId))) {
                    return;
                }

                if (mappedNewId !== null) {
                    usedNewIds.add(toIdKey(mappedNewId));
                }

                nextMappings[toIdKey(mapping.oldServiceTaskId)] = mappedNewId;
            });

            setSelectedMappings(nextMappings);
            return;
        }

        const usedNewIds = new Set();
        const normalizedNewSteps = newSteps.map((step) => ({
            ...step,
            normalizedName: normalizeName(step.name),
        }));

        oldSteps.forEach((oldStep) => {
            const oldStepNormalizedName = normalizeName(oldStep.name);

            const nameMatch = normalizedNewSteps.find(
                (newStep) =>
                    !usedNewIds.has(toIdKey(newStep.id)) &&
                    newStep.normalizedName === oldStepNormalizedName,
            );

            if (nameMatch) {
                nextMappings[toIdKey(oldStep.id)] = nameMatch.id;
                usedNewIds.add(toIdKey(nameMatch.id));
                return;
            }

            const orderMatch = normalizedNewSteps.find(
                (newStep) =>
                    !usedNewIds.has(toIdKey(newStep.id)) &&
                    Number(newStep.order) === Number(oldStep.order),
            );

            if (orderMatch) {
                nextMappings[toIdKey(oldStep.id)] = orderMatch.id;
                usedNewIds.add(toIdKey(orderMatch.id));
                return;
            }

            nextMappings[toIdKey(oldStep.id)] = null;
        });

        setSelectedMappings(nextMappings);
    };

    const handleConfirm = () => {
        const payload = oldSteps.map((oldStep) => ({
            oldServiceTaskId: oldStep.id,
            newServiceTaskId:
                typeof selectedMappings[toIdKey(oldStep.id)] === "undefined"
                    ? null
                    : selectedMappings[toIdKey(oldStep.id)],
        }));

        onConfirm(payload);
    };

    const handleCancel = () => {
        setOpen(false);
        onCancel();
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleCancel}>
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex items-end justify-center min-h-full p-4 text-center sm:items-center sm:p-0">
                        <Dialog.Panel className="relative w-full max-w-3xl p-6 overflow-hidden text-left transition-all transform bg-white rounded-lg shadow-xl">
                            <div className="flex items-start justify-between">
                                <Dialog.Title
                                    as="h3"
                                    className="text-lg font-medium leading-6 text-gray-900"
                                >
                                    Change pipeline — step mapping
                                </Dialog.Title>
                                <button
                                    type="button"
                                    className="text-sm text-gray-500 hover:text-gray-700"
                                    onClick={handleCancel}
                                >
                                    Close
                                </button>
                            </div>

                            <div className="grid gap-3 mt-4 md:grid-cols-3">
                                <div className="p-3 border rounded-md border-slate-200 bg-slate-50">
                                    <p className="text-sm font-medium text-slate-800">
                                        Complete
                                    </p>
                                    <p className="text-sm text-slate-600">
                                        Will NOT change
                                    </p>
                                    <p className="mt-1 text-lg font-semibold text-slate-800">
                                        {previewData?.completeCount ?? 0}
                                    </p>
                                </div>
                                <div className="p-3 border rounded-md border-slate-200 bg-slate-50">
                                    <p className="text-sm font-medium text-slate-800">
                                        In Progress
                                    </p>
                                    <p className="text-sm text-slate-600">
                                        Will be migrated using step mapping
                                    </p>
                                    <p className="mt-1 text-lg font-semibold text-slate-800">
                                        {previewData?.inProgressCount ?? 0}
                                    </p>
                                </div>
                                <div className="p-3 border rounded-md border-slate-200 bg-slate-50">
                                    <p className="text-sm font-medium text-slate-800">
                                        To Do
                                    </p>
                                    <p className="text-sm text-slate-600">
                                        Will be reset to the new pipeline
                                    </p>
                                    <p className="mt-1 text-lg font-semibold text-slate-800">
                                        {previewData?.toDoCount ?? 0}
                                    </p>
                                </div>
                            </div>

                            <div className="flex justify-end mt-4">
                                {canAutoMatch ? (
                                    <button
                                        type="button"
                                        onClick={handleAutoMatch}
                                        className="px-3 py-2 text-sm font-medium text-white rounded-md bg-slate-700 hover:bg-slate-800 focus:outline-none focus:ring-0"
                                    >
                                        Auto-match
                                    </button>
                                ) : null}
                            </div>

                            <div className="mt-4 space-y-3 overflow-y-auto max-h-80">
                                {oldSteps.map((oldStep) => {
                                    const usedNewStepIds = getUsedNewStepIds(
                                        oldStep.id,
                                    );
                                    const selectedValue =
                                        Object.prototype.hasOwnProperty.call(
                                            selectedMappings,
                                            toIdKey(oldStep.id),
                                        )
                                            ? selectedMappings[
                                                  toIdKey(oldStep.id)
                                              ] === null
                                                ? NO_MATCH_VALUE
                                                : String(
                                                      selectedMappings[
                                                          toIdKey(oldStep.id)
                                                      ],
                                                  )
                                            : "";

                                    return (
                                        <div
                                            key={oldStep.id}
                                            className="grid items-center gap-3 p-3 border rounded-md border-slate-200 md:grid-cols-2"
                                        >
                                            <div>
                                                <p className="text-sm font-medium text-slate-800">
                                                    {oldStep.name}
                                                </p>
                                                <p className="text-xs text-slate-500">
                                                    Old order: {oldStep.order}
                                                </p>
                                            </div>
                                            <div>
                                                <select
                                                    value={selectedValue}
                                                    onChange={(event) =>
                                                        handleChangeMapping(
                                                            oldStep.id,
                                                            event.target.value,
                                                        )
                                                    }
                                                    className="inline-flex justify-between w-full px-3 py-2 text-sm font-normal text-gray-700 truncate bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-0"
                                                >
                                                    <option value="" disabled>
                                                        Select a new step
                                                    </option>
                                                    <option value={NO_MATCH_VALUE}>
                                                        No match (archive)
                                                    </option>
                                                    {newSteps.map((newStep) => (
                                                        <option
                                                            key={newStep.id}
                                                            value={newStep.id}
                                                            disabled={usedNewStepIds.includes(
                                                                toIdKey(newStep.id),
                                                            )}
                                                        >
                                                            {newStep.name} (Order:{" "}
                                                            {newStep.order})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="flex justify-end mt-6 space-x-2">
                                <button
                                    type="button"
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-0"
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="px-4 py-2 text-sm font-medium text-white bg-teal-500 border border-transparent rounded-md hover:bg-teal-600 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
                                    onClick={handleConfirm}
                                    disabled={!hasSelectionForAllOldSteps}
                                >
                                    Confirm and appy
                                </button>
                            </div>
                        </Dialog.Panel>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
